<template>
  <div class="relative h-full">
    <!-- このエリアを検索ボタン -->
    <transition
      v-show="needNowAreaSearch"
      name="nowAreaSearch"
      class="absolute -translate-x-1/2 left-[50%] z-10"
    >
      <div
        class="flex w-37 h-[39px] justify-center items-center bg-white p-3 shadow-normal rounded-[25px] mt-4"
        @click="reSearchNowArea()"
      >
        <div class="text-W7 text-[15px] leading-[27px] text-primary">
          このエリアを検索
        </div>
      </div>
    </transition>
    <!-- 現在地アイコン -->
    <transition
      v-show="isShowGoBuckCurrentSpot"
      name="currentSpot"
      class="absolute right-5 z-10"
    >
      <div
        class="circle-back-current-position-button mt-4"
        @click="currentSpotFit()"
      >
        <img class="h-4 w-4 mx-auto" src="@/assets/current_location.svg" />
      </div>
    </transition>
    <!-- マップ -->
    <div
      id="nearbySearch-map"
      :name="mapElementId"
      ref="map"
      class="w-full h-full"
    />
    <!-- マップが操作できないよう、透過Layerをかぶせる -->
    <div v-if="!isOperable" class="absolute top-0 z-[30] h-full w-full" />
  </div>
</template>
<script>
import {META_TAB} from '@/router'
import Util from '@/mixins/util'
import {imageToBase64} from '@/utils/canvasUtil'
import log from 'loglevel'
// 地図上に表示するアイコン画像サイズ
const SPOT_ICON_SIZE = {
  height: 28,
  width: 28,
}

// 地図上に表示するアイコン画像サイズ
const SELECT_ICON_SIZE = {
  height: 42,
  width: 42,
}

// 現在地アイコンのサイズ
const CURRENT_LOCATION_ICON_SIZE = {
  height: 36,
  width: 36,
}

const NearbySearchMap = {
  name: 'NearbySearchMap',
  components: {},
  mixins: [Util],
  emits: [
    'moving-map',
    'stop-map',
    'click-research-now-area',
    'map-move',
    'tap-spot',
    'transition',
    'finish-generate-map',
  ],
  props: {
    // MapScriptを生成する際に付与するID
    mapElementId: String,
    // マップの中点をずらすためのオフセット情報
    centerOffset: {
      type: Object,
      default: {
        lat: 0,
        lon: 0,
      },
    },
    /** 投稿一覧 */
    spotList: {
      type: Array,
      required: true,
    },
    // マップへの操作ができるかどうか
    isOperable: {
      type: Boolean,
      default: true,
    },
    selectedCardInfo: {type: Object, default: {}}, // カードリストコンポーネントから選択されたカードのスポット情報
    // 検索範囲用のpadding情報
    mapRangePadding: {
      type: Object,
      default: {
        top: 0,
        right: 0,
        left: 0,
        bottom: 0,
      },
    },
    // カードリストのスクロール状態
    isScrollCardList: {
      type: Boolean,
      default: false,
    },
    // ページネーション中
    isPagination: {
      type: Boolean,
      default: false,
    },
    // スポットの取得失敗
    isFetchSpotFailed: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // VueのReactivityによる値変更検知をさせないため、オブジェクトを凍結
      frozen: Object.freeze({
        mapRelated: {
          map: null,
          addGlMarkerList: [], // マップアイコンリスト
          selectedGlMarker: null, // 選択したマップアイコン
        },
      }),
      isFirstLocationData: true, //初回の位置情報データかどうか
      userLocationData: null, // 自位置情報クラスのインスタンス
      isCurrentSpotCenter: true, // 現在地中心かどうか
      needNowAreaSearch: false, // このエリアを検索ボタン表示
      isStopNotice: false, // 親への通知を停止フラグ
      mapMoveTimer: null, // マップ操作停止タイマー変数
    }
  },
  activated() {
    if (this.frozen.mapRelated.map == null) {
      this.loadMapScript()
    }
  },
  computed: {
    /** 現在地 */
    currentPosition() {
      return this.$store.state.currentPosition
    },
    /**
     * 現在地に戻るアイコンを表示するかどうか
     * 下記条件を満たす場合に、アイコンを表示する
     * ・現在地が取得済みであること（現在地アイコンが生成されていること）
     * ・現在地がマップの中心で"ない"こと
     */
    isShowGoBuckCurrentSpot() {
      return !this.isCurrentSpotCenter && this.userLocationData
    },
  },
  watch: {
    /** 現在地が変更された場合、マップの現在地を更新 */
    currentPosition: {
      deep: true,
      handler() {
        this.updateCurrentPosition()
      },
    },
    /** スポット一覧が更新された場合、スポット一覧表示を更新 */
    spotList: {
      handler(afterList, beforeList) {
        if (!this.isExistsMap()) {
          return
        }
        // ページネーション時、スポットを取り切ったらアイコン更新不要
        if (this.isPagination && afterList.length === beforeList.length) {
          return
        }
        this.$nextTick(() => {
          if (this.isFetchSpotFailed) {
            this.frozen.mapRelated.selectedGlMarker.hide()
          } else {
            this.frozen.mapRelated.selectedGlMarker.show()
          }
          this.reloadSpotIcon()
        })
      },
    },
    selectedCardInfo: {
      async handler(value) {
        /* eslint-disable no-undef */
        if (this.isEmpty(value)) {
          this.frozen.mapRelated.selectedGlMarker.setProperties({})
          this.frozen.mapRelated.selectedGlMarker.hide()
        } else {
          // タップされた標準マーカーのスポット情報に更新
          this.frozen.mapRelated.selectedGlMarker.setProperties(value.spot)

          // 選択マーカーのタップされた標準ピンの座標に更新
          const coord = new mapscript.value.LatLng(
            value.spot.coord.lat,
            value.spot.coord.lon
          )
          this.frozen.mapRelated.selectedGlMarker.setPosition(coord)
          this.frozen.mapRelated.selectedGlMarker.show()

          // もし枠外のスポットのカードを選択した場合、FIT処理させる
          if (this.isOutOfRangeSpot(value.spot.coord)) {
            this.isStopNotice = true // FIT処理後、カードリストを表示したままにするため
            this.moveCenterPoint(value.spot.coord)
          }
        }
        /* eslint-enable no-undef */
      },
    },
  },
  methods: {
    /** Mapスクリプトの取得か再反映 */
    loadMapScript() {
      const mapScript = document.getElementById(this.mapElementId)
      if (this.isNull(mapScript)) {
        this.fetchMapScript()
      } else {
        let text = mapScript.text
        document.head.removeChild(mapScript)
        // あらかじめ取得しておいたデータを用いて新たにマップを追加する
        this.reflectMapScriptInElement(text)
        this.updateCurrentPosition()

        // アイコン配置
        this.setSpotIconList(this.spotList)
      }
    },

    /** NavitimeからMapスクリプトを取得 */
    fetchMapScript() {
      // 未生成の場合、マップを生成する
      let vm = this
      // 地図情報を取得する
      const success = (data) => {
        this.reflectMapScriptInElement(window.atob(data))
        this.updateCurrentPosition()
        vm.$store.commit('endLoading')
      }
      vm.$store.commit('startLoading')
      vm.$nextTick(function () {
        // 地図情報取得処理
        vm.$store.dispatch('getMapScript', {
          success,
        })
      })
    },

    /** Mapスクリプトを反映 */
    reflectMapScriptInElement(base64Script) {
      if (this.isNull(document.getElementById('nearbySearch-map'))) {
        return
      }
      let script = document.createElement('script')
      // スクリプトの加工・DOM追加
      script.setAttribute('type', 'text/javascript')
      script.setAttribute('id', this.mapElementId)
      script.text = base64Script
      document.head.appendChild(script)
      this.init()
    },

    /** マップ情報の初期設定 */
    init() {
      /* eslint-disable no-undef */
      const lat =
        this.currentPosition.lat || this.$config.DEFAULT_CURRENT_POSITION.LAT

      const lon =
        this.currentPosition.lon || this.$config.DEFAULT_CURRENT_POSITION.LON
      const centerOffsetLat = this.centerOffset.lat
      const centerOffsetLon = this.centerOffset.lon

      // ストアに位置情報がない場合は位置情報を取得
      this.frozen.mapRelated.map = new mapscript.Map(
        process.env.VUE_APP_NAVITIME_CLIENT_ID,
        {
          target: '#nearbySearch-map',
          // 初期値は東京駅
          center: new mapscript.value.LatLng(lat, lon),
          // 縮尺設定
          zoomLevel: this.$config.MAP_ZOOMLEVEL.ZOOM_200_M,
          options: {
            // 中心位置をずらす
            centerOffset: new mapscript.value.Point(
              centerOffsetLat,
              centerOffsetLon
            ),
            // タイルの3Dビル表示設定を無効にする
            tileBuilding3D: false,
          },
        }
      )
      if (!this.isExistsMap()) {
        return
      }
      // マップサイズ変更イベントを設定(FlutterのWebViewではAndroidでのみ発火する)
      window.visualViewport.addEventListener('resize', this.onMapResize)

      // マップの中点に変更がある場合は、変更後の数値を親に通知
      this.frozen.mapRelated.map.addEventListener(
        'centermoved',
        this.movingMapEventFunc
      )
      // 選択ピンの準備
      this.createFirstMarker({
        name: 'selectedPin',
        coord: {
          lat: 0,
          lon: 0,
        },
      })
      this.$emit('finish-generate-map')
    },

    /**
     * 処理実行前にマップが非表示となることがあるため
     * dokoiku-mapが存在するか確認
     */
    isExistsMap() {
      return (
        !this.isNull(document.getElementById('nearbySearch-map')) &&
        !this.isNull(document.getElementById(this.mapElementId)) &&
        !this.isNull(this.frozen.mapRelated.map?.mapController)
      )
    },

    /** コンポーネントを破棄する際の初期化処理 */
    unmountProcess() {
      /** イベントリスナーの除去 */
      window.visualViewport.removeEventListener('resize', this.onMapResize)
      this.frozen.mapRelated.map.removeEventListener(
        'centermoved',
        this.movingMapEventFunc
      )
      this.frozen.mapRelated.selectedGlMarker.removeEventListener(
        'click',
        this.emitSpot
      )

      /** マップ関連のインスタンスの破棄 */
      this.removeAllSpotIcon()
      this.frozen.mapRelated.map.removeGLMarker(
        this.frozen.mapRelated.selectedGlMarker
      )
      this.frozen.mapRelated.map.destroy()
      this.frozen.mapRelated.map = null
      this.frozen.mapRelated.selectedGlMarker = null

      this.isFirstLocationData = true
      this.isCurrentSpotCenter = true
      this.needNowAreaSearch = false

      /** MapScriptのStyle要素を削除 */
      const mapStyleEl = document.getElementById('gaia-stylesheet')
      if (!mapStyleEl) return
      mapStyleEl.remove()
    },
    /**
     * マップサイズ変更イベント
     */
    onMapResize() {
      // マップが存在しない場合は何もしない
      if (!this.isExistsMap()) {
        return
      }
      const currentSize = this.frozen.mapRelated.map.getClientSize()
      const mapEle = document.getElementById('nearbySearch-map')
      // 現在のサイズと比較し、マップ表示可能領域が広くなっている場合のみリサイズする
      if (
        currentSize.height < mapEle.clientHeight ||
        currentSize.width < mapEle.clientWidth
      ) {
        const mapSize = new mapscript.value.Size(
          mapEle.clientHeight,
          mapEle.clientWidth
        )
        this.frozen.mapRelated.map.setClientSize(mapSize)
      }
    },

    /** 現在地表示の更新処理 */
    async updateCurrentPosition() {
      // インスタンス生成されていない場合はスキップ
      if (!this.isExistsMap()) {
        return
      }

      // storeの緯度経度がnullの場合はスキップ
      if (!this.currentPosition.lat || !this.currentPosition.lon) {
        return
      }

      // storeの現在地を取得
      const updateLatLon = new mapscript.value.LatLng(
        this.currentPosition.lat,
        this.currentPosition.lon
      )

      // ユーザーアイコンのインスタンスを作成・更新
      if (this.userLocationData == null) {
        // 初回はインスタンスを生成
        this.userLocationData = new mapscript.value.UserLocationData(
          updateLatLon, // 緯度軽度
          this.$config.ZERO // 方角
        )
      } else {
        // 作成済みの場合は緯度軽度を更新
        this.userLocationData.setLatlng(updateLatLon)
      }

      if (this.isFirstLocationData) {
        // 初回 位置情報更新フラグを落とす
        this.isFirstLocationData = false
        // 現在地アイコン読み込み
        const currentLocation = await imageToBase64(
          require('@/assets/IconCurrentLocation.svg'),
          CURRENT_LOCATION_ICON_SIZE
        )

        const info = new mapscript.value.GLMarkerIconInfo({
          icon: currentLocation, // 画像パス(base64文字列も可)
          size: CURRENT_LOCATION_ICON_SIZE, // 画像サイズ(指定なければデフォルト値が設定される)
        })

        // マップにユーザアイコンを配置
        this.frozen.mapRelated.map.setUserLocation(
          new mapscript.object.UserLocation({info: info})
        )
        // ユーザの位置情報を設定（アニメーションなし）
        this.frozen.mapRelated.map.setUserLocationData(
          this.userLocationData,
          false
        )
      } else {
        // ２回目以降 位置情報更新
        // ユーザの位置情報を設定（アニメーションあり）
        this.frozen.mapRelated.map.setUserLocationData(
          this.userLocationData,
          true
        )
      }
    },
    /**
     * マップ移動イベントの設定を行う
     * - このエリアを検索ボタンを表示
     * - 現在地アイコンの表示
     * - カードリストを画面外(下へ)に移動させる
     */
    movingMapEventFunc() {
      this.needNowAreaSearch = true
      this.isCurrentSpotCenter = false
      // マップ操作を停止してから、チェック
      clearTimeout(this.mapMoveTimer)
      this.mapMoveTimer = setTimeout(() => {
        if (this.isStopNotice) {
          this.isStopNotice = false // 通常のマップ操作では、通知が必要であるためここで解除
          return
        }
        this.$emit('map-move')
      }, 300) // eslint-disable-line no-magic-numbers
    },
    /** 現在地に画面を移動させる */
    currentSpotFit() {
      if (!this.isExistsMap()) {
        return
      }
      if (this.userLocationData) {
        const latLng = this.userLocationData.getLatlng()
        this.frozen.mapRelated.map.setCenter(latLng)
      }
      this.isCurrentSpotCenter = true
    },
    /**
     * 表示しているマップにて、スポットを再検索する
     */
    reSearchNowArea() {
      if (!this.isExistsMap() || this.isScrollCardList) return

      this.needNowAreaSearch = false
      this.$emit('click-research-now-area')
    },

    /**
     * 任意の場所に画面を移動させる
     * @param {Number} lat 緯度
     * @param {Number} lon 経度
     */
    callFitWithLatLon(lat, lon) {
      return this.frozen.mapRelated.map.callFitWithLatLon(lat, lon)
    },

    /** 中心座標の取得処理 */
    getCenter() {
      return this.frozen.mapRelated.map.getCenter()
    },

    /**
     * 表示中の4点座標を取得
     * 投稿データにて、マップの範囲内にあるスポットを取得するため
     * @param {object} padding マップ範囲の調整情報
     * @param {number|undefined} padding.top マップの上辺のオフセット値
     * @param {number|undefined} padding.right マップの右辺のオフセット値
     * @param {number|undefined} padding.bottom マップの下辺のオフセット値
     * @param {number|undefined} padding.left マップの左辺のオフセット値
     * @returns {object} 表示中のマップの左上、右下の座標
     */
    getMapRect(padding) {
      const map = this.frozen.mapRelated.map
      const mapSize = map.getClientSize()

      /* eslint-disable no-magic-numbers */
      const {top, right, bottom, left} = padding
      const paddingTop = top ?? 0
      const paddingRight = right ?? 0
      const paddingBottom = bottom ?? 0
      const paddingLeft = left ?? 0
      /* eslint-enable no-magic-numbers */

      const rectTL = map.getLatLngFromClientCoord({
        x: paddingLeft,
        y: paddingTop,
      })
      const rectTR = map.getLatLngFromClientCoord({
        x: mapSize.width - paddingRight,
        y: paddingTop,
      })
      const rectBL = map.getLatLngFromClientCoord({
        x: paddingLeft,
        y: mapSize.height - paddingBottom,
      })
      const rectBR = map.getLatLngFromClientCoord({
        x: mapSize.width - paddingRight,
        y: mapSize.height - paddingBottom,
      })

      // APIに連携する前に、小数点以下第7位以降を切り捨て
      const convertCoordSixDecimal = (value) => {
        // eslint-disable-next-line no-magic-numbers
        return Number.parseFloat(value).toFixed(6)
      }
      return {
        topLeft: {
          lat: convertCoordSixDecimal(rectTL.lat),
          lon: convertCoordSixDecimal(rectTL.lng),
        },
        topRight: {
          lat: convertCoordSixDecimal(rectTR.lat),
          lon: convertCoordSixDecimal(rectTR.lng),
        },
        bottomLeft: {
          lat: convertCoordSixDecimal(rectBL.lat),
          lon: convertCoordSixDecimal(rectBL.lng),
        },
        bottomRight: {
          lat: convertCoordSixDecimal(rectBR.lat),
          lon: convertCoordSixDecimal(rectBR.lng),
        },
      }
    },
    /**
     * スポット範囲外判定処理
     * @param {object} spotCoord 選択されたスポット情報
     * @param {number} spotCoord.lat 選択されたスポット情報
     * @param {number} spotCoord.lon 選択されたスポット情報
     * @returns {boolean} 範囲外である
     */
    isOutOfRangeSpot(spotCoord) {
      const [outOfRange, notOutOfRange] = [true, false]
      const {top, right, left, bottom} = this.mapRangePadding
      const {topLeft, bottomRight} = this.getMapRect({
        top: top,
        right: right,
        left: left,
        bottom: bottom,
      })
      // 左上の座標より外である
      if (topLeft.lat < spotCoord.lat || topLeft.lon > spotCoord.lon) {
        return outOfRange
      }
      // 右下の座標より外である
      if (bottomRight.lat > spotCoord.lat || bottomRight.lon < spotCoord.lon) {
        return outOfRange
      }
      return notOutOfRange
    },
    /**
     * スポットのマーカーをマップに設定
     * @param {array} spotList 投稿リスト
     */
    setSpotIconList(spotList) {
      if (this.isEmpty(spotList)) return

      /** スポット分のマーカーを準備 */
      const firstSpot = 0
      for (let index = firstSpot; index < spotList.length; index++) {
        let spot = spotList[index].spot

        // 標準マーカーインスタンス準備
        spot.index = index // カードとの同期をさせるためインデックス値を追加
        const glMarker = this.makeMapGLMarker(
          spot.coord.lat,
          spot.coord.lon,
          require('@/assets/dokoiku/IconMapPlace.svg'),
          spot.name,
          SPOT_ICON_SIZE,
          true,
          spot
        )

        // 標準ピンから選択ピンに切り替えるイベント付与
        glMarker.addEventListener('click', this.clickSpotIcon)

        this.frozen.mapRelated.addGlMarkerList.push(glMarker)
      }

      /** 用意したマーカーリストをマップに反映 */
      this.frozen.mapRelated.map.addGLMarkers(
        this.frozen.mapRelated.addGlMarkerList
      )
    },

    /**
     * GLMarkerを生成する
     * @param {Number} latitude 緯度
     * @param {Number} longitude 軽度
     * @param {String} icon 配置したいアイコン
     * @param {String} name アイコンにつける名前
     * @param {Object} size 縦横サイズ
     * @param {boolean} isVisible 表示状態
     * @param {Object} spotInfo スポット情報(地点詳細画面へ遷移時に必要)
     * @param {Number} zIndex マーカーのz-index値(初期値は)
     * @returns {GLMarker} 生成されたマーカーインスタンス
     */
    makeMapGLMarker(
      latitude,
      longitude,
      icon,
      name,
      size,
      isVisible,
      spotInfo,
      zIndex = 0 // eslint-disable-line no-magic-numbers
    ) {
      /** 表示する緯度経度 */
      const position = new mapscript.value.LatLng(latitude, longitude)

      /** マーカー情報 */
      const info = new mapscript.value.GLMarkerIconInfo({
        icon: icon, // 画像(base64文字列)
        size: size, // 画像サイズ(指定なければデフォルト値が設定される)
      })

      /** GLマーカーインスタンス生成 */
      const glMarker = new mapscript.object.GLMarker({
        position: position,
        info: info,
        isVisible: isVisible,
        name: name,
        zIndex: zIndex,
      })

      /** スポット情報を不要 */
      glMarker.setProperties(spotInfo)

      return glMarker
    },

    /**
     * 1件目のスポットマーカーを作成
     * 選択と標準を用意するため
     * @param {object} firstSpot 1件目のスポット情報
     */
    createFirstMarker(firstSpot) {
      // 選択ピンの作成
      const selectedGlMarker = this.makeMapGLMarker(
        firstSpot.coord.lat,
        firstSpot.coord.lon,
        require('@/assets/dokoiku/IconMapSelectPlace.svg'),
        firstSpot.name,
        SELECT_ICON_SIZE,
        true,
        firstSpot,
        10 // eslint-disable-line no-magic-numbers
      )

      // 地点詳細画面への遷移させるイベントを付与
      selectedGlMarker.addEventListener('click', this.emitSpot)

      // 選択マーカーインスタンスを保存
      this.frozen.mapRelated.selectedGlMarker = selectedGlMarker

      /** 用意した選択ピンインスタンスをマップに反映 */
      this.frozen.mapRelated.map.addGLMarker(selectedGlMarker)
    },

    /**
     * スポットアイコン押下時のクリック処理
     * @param {Event} タッチイベント
     */
    clickSpotIcon(event) {
      const {properties, position} = event.sourceObject

      // タップされた標準マーカーのスポット情報に更新
      this.frozen.mapRelated.selectedGlMarker.setProperties(properties)

      // 選択マーカーのタップされた標準ピンの座標に更新
      this.frozen.mapRelated.selectedGlMarker.setPosition(position)

      // 枠外のピンをタップしていた場合は、FIT処理させる
      if (this.isOutOfRangeSpot(properties.coord)) {
        this.moveCenterPoint(properties.coord)
      }

      // カードコンポーネントに情報連携
      this.$emit('tap-spot', properties)
    },

    /** スポットマーカの再描画 */
    reloadSpotIcon() {
      // 表示中のスポットアイコンを削除
      this.removeAllSpotIcon()
      // 再取得したデータでアイコン再配置
      this.setSpotIconList(this.spotList)
    },

    /** 表示中スポットマーカーを全て削除 */
    removeAllSpotIcon() {
      // マーカーに付与したイベントの削除
      this.frozen.mapRelated.addGlMarkerList.forEach((glMarker) => {
        glMarker.removeEventListener('click', this.clickSpotIcon)
      })

      // Map側にあるマーカーを削除
      this.frozen.mapRelated.map.removeGLMarkers(
        this.frozen.mapRelated.addGlMarkerList
      )

      // メモリ側を初期化
      this.frozen.mapRelated.addGlMarkerList = []
    },
    /**
     * スポットの座標位置をにマップ中点としてマップ移動する
     * @param {object} target 対象の座標情報
     */
    moveCenterPoint(target) {
      if (!this.isExistsMap()) {
        return
      }
      const spotLatLon = new mapscript.value.LatLng(target.lat, target.lon)
      // アニメーションは有効にする
      this.frozen.mapRelated.map.setCenter(spotLatLon, true)
      this.isStopNotice = true
    },
    /**
     * 選択ピン押下
     * @param {object} value 選択ピンが持っているスポット情報
     */
    emitSpot(value) {
      // 選択ピンをタップしてもスクロール中であれば処理終了
      if (this.isScrollCardList) return

      // スポット情報を連携
      const selectedSpot = value.sourceObject.properties
      this.$emit('transition', selectedSpot)
    },
  },
}

export default NearbySearchMap
</script>
<style scoped>
#dokoiku-map {
  height: 100vh;
}

/* 「このエリアを検索」「現在地アイコン」の表示時アニメーション(表示) */
.currentSpot-enter-active,
.currentSpot-leave-active,
.nowAreaSearch-enter-active,
.nowAreaSearch-leave-active {
  transition: opacity 0.5s ease;
}

/* 「このエリアを検索」「現在地アイコン」の表示時アニメーション(消える) */
.currentSpot-enter-from,
.currentSpot-leave-to,
.nowAreaSearch-enter-from,
.nowAreaSearch-leave-to {
  opacity: 0;
}
</style>
